import { ReactElement, CSSProperties, memo } from 'react'
import PropTypes from 'prop-types'

import SectionTitle from '@/components/SectionTitle'
import List from '@/components/List'
import Button from '@/components/Button'

import { WidgetContentPropType, ExampleDocPropType } from '@/propTypes'
import { ReactComponent as Group13 } from '@/svg/decorators/group-13.svg'

import env from '@/lib/env'
import { logEvent } from '@/lib/gtm'

import Widget from '@/features/landing/components/WhatInProkit/Widget'
import EgDocument from '@/features/landing/components/WhatInProkit/EgDocument'

import Container from './style'

interface Props {
  documentsLink: string
  documents: ExampleDoc[]
  items: WidgetContentInterface[]
  style?: CSSProperties
  className?: string
  id?: string
  widgetId?: string
  documentId?: string
}

function WhatInProKit({
  id,
  className,
  style,
  items,
  documents,
  documentsLink,
  widgetId,
  documentId,
}: Props): ReactElement {
  return (
    <Container id={id} className={className} style={style}>
      <div className="container">
        <div className="decorator-container">
          <Group13 id="group-13" />
        </div>
        <section id={widgetId} className="widget-container">
          <SectionTitle className="title-widget">
            <span>
              มีอะไรบ้างใน{' '}
              <span className="tertiary">
                PROKIT
                <svg
                  viewBox="0 0 100 31"
                  className="rectangle"
                  preserveAspectRatio="none"
                >
                  <rect x="0" y="16" width="100" height="2" />
                </svg>
                <svg viewBox="0 0 31 31" className="circle">
                  <circle cx="16" cy="16" r="15" />
                </svg>
              </span>
            </span>
          </SectionTitle>
          <section className="grid-container">
            {items.map((item: WidgetContentInterface) => (
              <Widget key={item.title} symbol={item.symbol} title={item.title}>
                {item.content}
              </Widget>
            ))}
          </section>
        </section>
        <section id={documentId} className="document-container">
          <SectionTitle className="title-document">
            <span>
              ตัวอย่างเอกสารใน PDPA&nbsp;
              <span className="tertiary">PROKIT</span>
            </span>
            <span className="subtitle">
              (ชุดเอกสารฉบับเต็มจะสามารถดาวน์โหลดได้ทันทีหลังจากซื้อ)
            </span>
          </SectionTitle>
          <h3 className="subtitle"></h3>
          <List>
            {documents.map((item: ExampleDoc) => (
              <EgDocument
                key={item.titleTH}
                href={item.href}
                titleTH={item.titleTH}
                titleEN={item.titleEN}
              />
            ))}
          </List>
          <a
            href={documentsLink}
            target="_blank"
            rel="noreferrer"
            className="documents-link"
            onClick={() => {
              logEvent({
                category: 'Document',
                action: 'Click Download',
              })
            }}
          >
            ดูเอกสารที่มีทั้งหมดใน PDPA Prokit <span>(ดาวน์โหลดฟรี!)</span>
          </a>
          <a
            href={env.PLATFORM_URL}
            target="_blank"
            rel="noreferrer"
            className="platform-link"
          >
            <Button
              isPrimary={true}
              usePrimaryColor={true}
              onPress={() => {
                logEvent({
                  category: 'Document',
                  action: 'Click Purchase',
                })
              }}
            >
              ซื้อชุดเอกสาร
            </Button>
          </a>
        </section>
      </div>
    </Container>
  )
}

WhatInProKit.propTypes = {
  documentsLink: PropTypes.string,
  documents: PropTypes.arrayOf(ExampleDocPropType),
  items: PropTypes.arrayOf(WidgetContentPropType),
  style: PropTypes.object,
  className: PropTypes.string,
  id: PropTypes.string,
  widgetId: PropTypes.string,
  documentId: PropTypes.string,
}

WhatInProKit.defaultProps = {
  className: '',
}

export default memo(WhatInProKit)
